"use client";

import { getAgentApiHost } from "@/lib/agent-api-host";

import { useEffect, useSyncExternalStore } from "react";

import { useBrowseMenu } from "@/hooks/useBrowseMenu";
import { projectBrowseMenuRoots, type BrowseMenuRootSummary } from "@/lib/browse-menu-roots";

type RootState = {
  roots: BrowseMenuRootSummary[] | null;
  status: "idle" | "loading" | "ready" | "error";
};
const IDLE: RootState = { roots: null, status: "idle" };

function createRootRecovery() {
  let state = IDLE;
  let pending: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const publish = (next: RootState) => {
    state = next;
    listeners.forEach((fn) => fn());
  };
  return {
    snapshot: () => state,
    subscribe: (fn: () => void) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    load() {
      if (pending) return pending;
      if (state.roots !== null) return Promise.resolve();
      pending = Promise.resolve()
        .then(async () => {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 5000);
          try {
            const url = new URL("/shop-agent/browse-menu?root_nodes_only=true", getAgentApiHost());
            const response = await fetch(url, { credentials: "omit", signal: controller.signal });
            if (!response.ok) throw new Error("Root request failed");
            publish({ roots: projectBrowseMenuRoots(await response.json()), status: "ready" });
          } catch {
            publish({ roots: null, status: "error" });
          } finally {
            clearTimeout(timeout);
          }
        })
        .finally(() => {
          pending = null;
        });
      publish({ roots: null, status: "loading" });
      return pending;
    },
  };
}
let recovery: ReturnType<typeof createRootRecovery> | undefined;
const resource = () =>
  typeof window === "undefined" ? undefined : (recovery ??= createRootRecovery());
const subscribe = (fn: () => void) => resource()?.subscribe(fn) ?? (() => {});
const snapshot = () => resource()?.snapshot() ?? IDLE;
const serverSnapshot = () => IDLE;

export function useNavigationMenu(initialRoots: BrowseMenuRootSummary[] | null) {
  const menu = useBrowseMenu({ enabled: false });
  const recovered = useSyncExternalStore(subscribe, snapshot, serverSnapshot);
  useEffect(() => {
    if (initialRoots === null && !menu.index && resource()?.snapshot().status === "idle") {
      void resource()?.load();
    }
  }, [initialRoots, menu.index]);
  return {
    ...menu,
    roots: menu.index ? menu.roots : (initialRoots ?? recovered.roots ?? []),
    rootsLoading:
      initialRoots === null && !menu.index && ["idle", "loading"].includes(recovered.status),
    rootsError: initialRoots === null && !menu.index && recovered.status === "error",
    retryRoots: () => {
      void resource()?.load();
    },
  };
}
